<template>
  <!-- Overlaps the masthead's lower edge, so the night card slides under it. -->
  <section class="readout">
    <div class="container">
      <div
        ref="stripRef"
        class="strip glass"
        :class="{ 'is-armed': armed, 'is-live': live }"
        aria-live="off"
      >
        <div
          v-for="(s, i) in stats"
          :key="s.key"
          class="cell"
          :style="{ '--i': i }"
        >
          <span class="value" :data-value="s.value">{{ s.value }}</span>
          <span class="label">{{ $t(s.key) }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted, onBeforeUnmount, ref } from "vue";
import projects from "@/data/projects.json";

const REDUCED =
  typeof matchMedia === "function" &&
  matchMedia("(prefers-reduced-motion: reduce)").matches;

// Count-up duration per cell, and the gap between one cell and the next.
// Six cells therefore resolve left to right in 1100 + 5 × 70 = 1450ms.
const DURATION = 1100;
const STEP = 70;

// Same contract as the original animateStat: a leading non-digit prefix (#),
// a numeric core, a trailing non-digit suffix (× +). Only the core counts.
const parseValue = (raw) => {
  const match = raw.match(/^([^\d]*)(\d+)([^\d]*)$/);
  if (!match) return null;
  return { prefix: match[1], target: parseInt(match[2], 10), suffix: match[3] };
};

export default {
  name: "ReadoutStrip",
  setup() {
    const stripRef = ref(null);

    // `armed` decides, at first render, whether the dividers start undrawn.
    // Deciding it in setup rather than onMounted means the strip is never
    // painted once with drawn dividers and then reset.
    const armed = !REDUCED && typeof IntersectionObserver !== "undefined";
    const live = ref(false);

    let observer = null;
    let raf = null;

    // Three of the six are measured off the data, so adding a project or
    // shipping a live URL updates the record without anyone editing copy.
    const activeCount = projects.filter((p) => p.active !== false).length;
    const liveCount = projects.filter(
      (p) => p.liveUrl && p.liveUrl !== "#"
    ).length;

    const stats = [
      { key: "readout.projects", value: String(activeCount) },
      { key: "readout.years", value: "6" },
      { key: "readout.appStore", value: "#10" },
      { key: "readout.live", value: String(liveCount) },
      { key: "readout.institutions", value: "4" },
      { key: "readout.cumLaude", value: "2×" },
    ];

    // ONE rAF drives all six count-ups. The stagger lives in the progress
    // term (`elapsed − i × STEP`), not in six timers and not in six loops —
    // §8's per-element-loop ban applies to the arrival as much as to hover.
    // Because every cell is written on the very first frame, the whole strip
    // resets to its zero form in a single paint and then resolves left to
    // right; no cell is ever seen jumping backwards off its final value.
    const runSequence = (cells) => {
      const parts = cells.map((el) => {
        const raw = el.getAttribute("data-value") || "";
        return { el, raw, num: parseValue(raw), last: null };
      });
      const span = DURATION + STEP * (parts.length - 1);
      const start = performance.now();

      const tick = (now) => {
        const elapsed = now - start;
        for (let i = 0; i < parts.length; i += 1) {
          const c = parts[i];
          let text;
          if (!c.num) {
            text = c.raw;
          } else {
            const t = Math.min(
              1,
              Math.max(0, (elapsed - i * STEP) / DURATION)
            );
            const eased = 1 - Math.pow(1 - t, 3);
            text = `${c.num.prefix}${Math.round(c.num.target * eased)}${
              c.num.suffix
            }`;
          }
          if (text !== c.last) {
            c.el.textContent = text;
            c.last = text;
          }
        }
        // Once the sequence is spent the strip is permanently static (§6),
        // so the loop stops existing rather than existing and doing nothing.
        raf = elapsed < span ? requestAnimationFrame(tick) : null;
      };

      raf = requestAnimationFrame(tick);
    };

    onMounted(() => {
      // The true values are already in the DOM and the dividers are already
      // drawn, so reduced motion needs no observer at all — it is never
      // created rather than created and idle.
      if (!armed) return;

      const strip = stripRef.value;
      if (!strip) return;
      // Read in document order. Vue's docs are explicit that a `ref` inside
      // v-for gives no ordering guarantee, and the stagger is index-ordered
      // — the seam order and the count-up order must not be able to differ.
      const cells = Array.from(strip.querySelectorAll("[data-value]"));
      if (!cells.length) return;

      // One observer on the strip, not one per cell: the six cells arrive as
      // a single event, so there is a single trigger.
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          observer = null;
          live.value = true; // hands the dividers to CSS
          runSequence(cells);
        },
        { threshold: 0.4 }
      );
      observer.observe(strip);
    });

    onBeforeUnmount(() => {
      if (observer) observer.disconnect();
      if (raf !== null) cancelAnimationFrame(raf);
    });

    return { stripRef, stats, armed, live };
  },
};
</script>

<style scoped>
/* position is declared here, not inherited from App.vue's global
   `section { position: relative; z-index: 1 }` — z-index: 3 is the whole
   point of §3.2 (the night card slides *under* the strip) and it must not
   depend on a rule in another file staying positioned. */
.readout {
  position: relative;
  margin-top: -28px;
  z-index: 3;
}

.strip {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  border-radius: var(--r-lg);
}

/* Warm glass. The shared --glass-fill is built from pure white, which over
   warm paper reads as a grey slab rather than as glass; these whites carry
   the ground's own warmth, so the pane looks like glass sitting on paper.
   The blur itself still comes from the shared .glass class — no second
   backdrop-filter is declared here. */
.strip {
  background-image: linear-gradient(
    128deg,
    rgba(255, 253, 248, 0.88) 0%,
    rgba(250, 245, 237, 0.6) 54%,
    rgba(255, 252, 246, 0.76) 100%
  );
  box-shadow: inset 0 1px 0 rgba(255, 254, 251, 0.9),
    0 0 0 0.5px rgba(22, 19, 15, 0.07), 0 0 0 1px rgba(255, 252, 246, 0.45),
    0 1px 2px rgba(22, 19, 15, 0.04), 0 12px 34px rgba(22, 19, 15, 0.1);
}

.cell {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-5) var(--s-4);
  min-width: 0;
}

/* The dividers are the strip's structure — one hairline per seam, the CV's
   .kpi + .kpi pattern, drawn as a pseudo-element so it can be transformed.
   It fades out at its lower end, so the draw runs out rather than stopping. */
.cell::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--hair-w);
  background: linear-gradient(
    180deg,
    transparent 0%,
    var(--hairline) 9%,
    var(--hairline) 74%,
    transparent 100%
  );
  transform-origin: top;
  transition: transform 260ms var(--ease-out);
  transition-delay: calc(var(--i, 0) * 70ms);
}
/* First cell sits against the pane's own left edge. */
.cell:first-child::before {
  display: none;
}

/* Each divider draws downward as its own numeral starts counting — same
   --i, same 70ms step. A divider is decoration, so it may be transformed;
   the numerals it separates never move. */
.strip.is-armed .cell::before {
  transform: scaleY(0);
}
.strip.is-armed.is-live .cell::before {
  transform: scaleY(1);
}

.value {
  font-size: 34px;
  font-weight: 650;
  line-height: 1;
  letter-spacing: -0.02em;
  /* Tabular figures: the count-up changes glyph widths every frame and this
     is what keeps the numeral, and the cell beside it, perfectly still. */
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.cell .label {
  color: var(--quiet);
  line-height: 1.3;
}

@media (max-width: 1100px) {
  .value {
    font-size: 28px;
  }
}

@media (max-width: 720px) {
  .strip {
    grid-template-columns: repeat(2, 1fr);
  }
  /* Two columns: the seam runs along the top of each row instead, and the
     same staggered draw reads left to right, then down. */
  .cell {
    padding: var(--s-4);
  }
  .cell::before {
    left: 0;
    right: 0;
    top: 0;
    bottom: auto;
    width: auto;
    height: var(--hair-w);
    background: linear-gradient(
      90deg,
      var(--hairline) 0%,
      var(--hairline) 74%,
      transparent 100%
    );
    transform-origin: left;
  }
  /* The first row sits against the strip's own top edge. */
  .cell:nth-child(-n + 2)::before {
    display: none;
  }
  .strip.is-armed .cell::before {
    transform: scaleX(0);
  }
  .strip.is-armed.is-live .cell::before {
    transform: scaleX(1);
  }
}

/* Belt and braces: `armed` is already false under reduced motion, so this
   only guards a user who flips the setting after the page has loaded. */
@media (prefers-reduced-motion: reduce) {
  .cell::before,
  .strip.is-armed .cell::before {
    transform: none;
    transition: none;
  }
}
</style>
